import resourcesService from '@/modules/resources/services/resourcesService';

const RESOURCE_TYPES_PAGE_SIZE = 5;
const RESOURCES_PAGE_SIZE = 100;

const initialState = {
  resourceTypes: [],
  resources: [],
  loading: false,
  resourceTypesLoading: false,
  currentPageIndex: 1,
  resourceTypesPageIndex: 1,
  completed: false,
  resourceTypesCompleted: false,
  refreshResourceTypes: false,
  refreshResources: false,
};

const state = { ...initialState };

const mutations = {
  addResourceTypes(state, resourceTypes) {
    state.resourceTypes = state.resourceTypes.concat(resourceTypes);
  },
  setResourceTypesLoading(state, loading) {
    state.resourceTypesLoading = loading;
  },
  setResourceTypesCompleted(state, completed) {
    state.resourceTypesCompleted = completed;
  },
  increaseResourceTypesPageIndex(state) {
    state.resourceTypesPageIndex += 1;
  },
  setRefreshResourceTypes(state, refreshResourceTypes) {
    state.refreshResourceTypes = refreshResourceTypes;
  },
  updateResourceType(state, updatedResourceType) {
    const index = state.resourceTypes.findIndex((resourceType) => resourceType.uid === updatedResourceType.uid);
    if (index !== -1) {
      state.resourceTypes.splice(index, 1, updatedResourceType);
    }
  },
  addResourceType(state, resourceType) {
    state.resourceTypes.unshift(resourceType);
  },
  removeResourceType(state, uid) {
    state.resourceTypes = state.resourceTypes.filter((resourceType) => resourceType.uid !== uid);
  },
  addResources(state, resources) {
    state.resources = state.resources.concat(resources);
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setCompleted(state, completed) {
    state.completed = completed;
  },
  increaseCurrentPageIndex(state) {
    state.currentPageIndex += 1;
  },
  setRefreshResources(state, refreshResources) {
    state.refreshResources = refreshResources;
  },
  updateResource(state, updatedResource) {
    const index = state.resources.findIndex((resource) => resource.uid === updatedResource.uid);
    if (index !== -1) {
      state.resources.splice(index, 1, updatedResource);
    }
  },
  addResource(state, resource) {
    state.resources.push(resource);
  },
  removeResource(state, uid) {
    state.resources = state.resources.filter((resource) => resource.uid !== uid);
  },
  clearData(state) {
    Object.assign(state, initialState);
  },
  clearResourceTypes(state) {
    state.resourceTypes = [];
    state.resourceTypesPageIndex = 1;
    state.resourceTypesCompleted = false;
    state.refreshResourceTypes = false;
  },
  clearResources(state) {
    state.resources = [];
    state.currentPageIndex = 1;
    state.completed = false;
    state.refreshResources = false;
  },
};
const actions = {
  async createResourceType({ commit, dispatch }, resourceType) {
    const res = await resourcesService.createResourceType(resourceType);
    const { success, data: responseResourceType } = res;
    if (success) {
      commit('setRefreshResources', true);
      await dispatch('loadMoreResources');
      commit('setRefreshResources', false);
    }
    commit('addResourceType', responseResourceType);
    return responseResourceType;
  },
  async updateResourceType({ commit }, { uid, resourceType }) {
    const res = await resourcesService.updateResourceType(uid, resourceType);
    commit('updateResourceType', res);
    return res;
  },
  async deleteResourceType({ commit }, uid) {
    const resourceTypeToDelete = state.resourceTypes.find((resourceType) => resourceType.uid === uid);
    try {
      commit('removeResourceType', uid);
      await resourcesService.deleteResourceType(uid);
    } catch (e) {
      const isResourceTypeDeleted = !state.resourceTypes.some((resourceType) => resourceType.uid === uid);
      if (isResourceTypeDeleted) {
        commit('addResourceType', resourceTypeToDelete);
      }
      throw e;
    }
  },
  async loadResourceTypes({ state, dispatch }) {
    if (state.resourceTypes.length && !state.refreshResourceTypes) return;
    await dispatch('loadMoreResourceTypes');
  },
  async loadMoreResourceTypes({ commit, state }) {
    if (state.refreshResourceTypes) {
      commit('clearResourceTypes');
    }
    if (state.resourceTypesCompleted || state.resourceTypesLoading) return;
    commit('setResourceTypesLoading', true);

    try {
      const resourceTypes = await resourcesService.fetchResourceTypes(
        state.resourceTypesPageIndex,
        RESOURCE_TYPES_PAGE_SIZE
      );
      commit('addResourceTypes', resourceTypes || []);
      commit('increaseResourceTypesPageIndex');
      if (!resourceTypes || resourceTypes.length < RESOURCE_TYPES_PAGE_SIZE) {
        commit('setResourceTypesCompleted', true);
      }
    } catch (e) {
      console.error('Error loading resource types', e);
    }

    commit('setResourceTypesLoading', false);
  },
  setRefreshResourceTypes({ commit }, refreshResourceTypes) {
    commit('setRefreshResourceTypes', refreshResourceTypes);
  },
  async createResource({ commit }, resource) {
    const res = await resourcesService.createResource(resource);
    commit('addResource', res);
    return res;
  },
  async updateResource({ commit }, { uid, resource }) {
    const res = await resourcesService.updateResource(uid, resource);
    commit('updateResource', res);
    return res;
  },
  async deleteResource({ commit, state }, uid) {
    const resourceToDelete = state.resources.find((resource) => resource.uid === uid);
    commit('removeResource', uid);
    try {
      await resourcesService.deleteResource(uid);
    } catch (error) {
      const isResourceDeleted = !state.resources.some((resource) => resource.uid === uid);
      if (isResourceDeleted) {
        commit('addResource', resourceToDelete);
      }
      throw error;
    }
  },
  async loadResources({ state, dispatch }, filter = {}) {
    if (filter.resource_type_uid) {
      const resourcesForType = state.resources.filter((resource) => resource.resource_type_uid === filter.resource_type_uid);
      if (resourcesForType.length > 0 && !state.refreshResources) return;
    } else if (state.resources.length && !state.refreshResources) {
      return;
    }
    
    await dispatch('loadMoreResources', filter);
  },
  async loadMoreResources({ commit, state }, filter = {}) {
    if (state.refreshResources) {
      commit('clearResources');
    }
    if (state.completed || state.loading) return;
    commit('setLoading', true);

    try {
      const resources = await resourcesService.fetchResources(
        state.currentPageIndex,
        RESOURCES_PAGE_SIZE,
        filter
      );
      commit('addResources', resources || []);
      commit('increaseCurrentPageIndex');
      if (!resources || resources.length < RESOURCES_PAGE_SIZE) {
        commit('setCompleted', true);
      }
    } catch (e) {
      console.error('Error loading resources', e);
    }

    commit('setLoading', false);
  },
  setRefreshResources({ commit }, refreshResources) {
    commit('setRefreshResources', refreshResources);
  },

  async bulkCreateResources({ commit }, resources) {
    const results = await resourcesService.bulkCreateResources(resources);
    const successfulResources = results
      .filter((result) => result.success && result.entity)
      .map((result) => result.entity);
    
    if (successfulResources.length > 0) {
      commit('addResources', successfulResources);
    }
    
    return results;
  },
};

const getters = {
  resourceTypes: (state) => state.resourceTypes,
  getResourceTypeByUid: (state) => (uid) => state.resourceTypes.find((resourceType) => resourceType.uid === uid),
  resourceTypesLoading: (state) => state.resourceTypesLoading,
  resourceTypesCompleted: (state) => state.resourceTypesCompleted,

  resources: (state) => state.resources,
  getResourceByUid: (state) => (uid) => state.resources.find((resource) => resource.uid === uid),
  getResourcesByType: (state) => (resourceTypeUid) => state.resources.filter((resource) => resource.resource_type_uid === resourceTypeUid),
  loading: (state) => state.loading,
  completed: (state) => state.completed,

  allDataLoaded: (state) => state.completed && state.resourceTypesCompleted,
  anyLoading: (state) => state.loading || state.resourceTypesLoading,
  
  resourcesHelpUrl: (state, localGetters, rootState, rootGetters) => {
    const supportLink = rootGetters['BusinessStore/supportArticleUrl']('Resources');
    const attributes = rootGetters['BusinessStore/attributes'];
    const locale = attributes?.locale?.toLowerCase() || 'en-us';
    const hasVariantLink = rootGetters['BusinessStore/hasFeature']('show_variant_learn_more_link');
    
    if (hasVariantLink) {
      return supportLink || '';
    }
    
    const isPartnerDirectory = rootGetters['BusinessStore/isPartnerDirectory'];
    const fallbackLink = isPartnerDirectory
      ? `https://support.myclients.io/hc/${locale}/articles/35641934380183-How-to-Use-Resources`
      : `https://support.vcita.com/hc/${locale}/articles/35488610459671-How-to-Use-Resources`;
    
    return supportLink || fallbackLink;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}; 
